import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "proxima.token";
const CUSTOMER_KEY = "proxima.customer";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY)),
  getCustomer: () => localStorage.getItem(CUSTOMER_KEY),
  setCustomer: (c) => (c ? localStorage.setItem(CUSTOMER_KEY, c) : localStorage.removeItem(CUSTOMER_KEY)),
};

export const api = axios.create({ baseURL: API_URL, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const customer = tokenStore.getCustomer();
  if (customer) config.headers["X-Customer-Id"] = customer;
  return config;
});

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && onUnauthorized) onUnauthorized();
    return Promise.reject(error);
  },
);

export const errorMessage = (error, fallback = "Noe gikk galt. Prøv igjen.") => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.message === "Network Error" ? "Får ikke kontakt med tjenesten." : fallback;
};

function authHeaders() {
  const h = { "Content-Type": "application/json" };
  const token = tokenStore.get();
  if (token) h.Authorization = `Bearer ${token}`;
  const customer = tokenStore.getCustomer();
  if (customer) h["X-Customer-Id"] = customer;
  return h;
}

/** Download a file (CSV/XLSX) through an authenticated request. */
export async function downloadFile(path, params = {}) {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))).toString();
  const res = await fetch(`${API_URL}${path}${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
  if (!res.ok) {
    let detail = "Eksport feilet";
    try {
      detail = (await res.json()).detail || detail;
    } catch (e) {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const filename = m ? m[1] : "eksport";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return filename;
}

/** Consume a POST SSE stream. Calls onEvent for each parsed event, onClose when the stream ends. Returns an abort function. */
export function streamSse(path, body, onEvent, onError, fallbackError = "Tjenesten er ikke tilgjengelig akkurat nå.", onClose) {
  const controller = new AbortController();
  (async () => {
    let aborted = false;
    try {
      const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        let detail = fallbackError;
        try {
          detail = (await res.json()).detail || detail;
        } catch (e) {
          /* ignore */
        }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = raw.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            onEvent(JSON.parse(line.slice(5)));
          } catch (e) {
            /* ignore malformed chunk */
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") aborted = true;
      else onError?.(err);
    } finally {
      onClose?.({ aborted });
    }
  })();
  return () => controller.abort();
}

/** Data assistant (constrained, deterministic). */
export const streamAssistant = (body, onEvent, onError) => streamSse("/assistant/ask", body, onEvent, onError, "Assistenten er ikke tilgjengelig akkurat nå.");

/** AI Workspace message stream for a conversation. */
export const streamWorkspace = (conversationId, body, onEvent, onError, onClose) => streamSse(`/workspace/conversations/${conversationId}/messages`, body, onEvent, onError, "AI Workspace er ikke tilgjengelig akkurat nå.", onClose);
